import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const ServiceCard = ({ service, index, variant = 'default' }) => {
  const { id, title, description, image } = service;

  if (variant === 'compact') {
    return (
      <motion.div
        data-testid={`service-card-${id}`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
      >
        <Link
          to={`/services#${id}`}
          className="service-card group relative overflow-hidden bg-secondary aspect-[3/4] flex flex-col justify-between block"
        >
          {/* Background Image */}
          {image && (
            <div className="absolute inset-0">
              <img 
                src={image} 
                alt={title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            </div>
          )}
          
          {/* Content */}
          <div className="relative z-10 p-6 md:p-8 flex flex-col justify-between h-full">
            <span className="eyebrow text-white/70 mb-4 block">
              {String(index + 1).padStart(2, '0')}
            </span>
            
            <div className="mt-auto">
              <h3 className="text-xl md:text-2xl font-medium tracking-tight font-['DM_Sans'] mb-3 text-white">
                {title}
              </h3>
              <p className="text-sm text-white/70 line-clamp-2">
                {description}
              </p>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mt-4 group-hover:bg-white group-hover:text-black transition-all duration-300">
                <ArrowUpRight className="w-4 h-4 text-white group-hover:text-black" />
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid={`service-card-full-${id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group"
    >
      <Link
        to={`/services#${id}`}
        className="block border-b border-border py-8 md:py-12 hover:bg-secondary/50 transition-colors px-4 md:px-8 -mx-4 md:-mx-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center">
          <div className="md:col-span-1">
            <span className="eyebrow">{String(index + 1).padStart(2, '0')}</span>
          </div>
          <div className="md:col-span-4">
            <h3 className="text-2xl md:text-3xl font-medium tracking-tight font-['DM_Sans'] group-hover:text-primary transition-colors">
              {title}
            </h3>
          </div>
          <div className="md:col-span-5">
            <p className="text-muted-foreground">{description}</p>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <div className="w-12 h-12 rounded-full border border-border flex items-center justify-center group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-all">
              <ArrowUpRight className="w-5 h-5" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ServiceCard;
